/**
 * The engine-side debug bridge, as a shell-side client.
 *
 * Inventory and rules-dependent character changes are performed here. Nested
 * objects cannot be encoded safely by the codec, while SPECIAL, XP, skills and
 * perks need the engine's current traits, perks, settings and progression code.
 *
 * ## Why this is a batch, and why it is async
 *
 * The bridge is a **command mailbox**, not four callable functions (engine
 * `388373a1`). The web build links with `-sASYNCIFY`, and while the engine sits
 * on the main menu it is parked inside `sharedFpsLimiter.throttle()` mid-`SDL_Delay`
 * with an unwound Asyncify stack still live — `Asyncify.state` reads `Normal`
 * but `Asyncify.currData` is non-zero. A synchronous JS→wasm call into anything
 * instrumented starts a second unwind over that parked buffer and traps the
 * module with `memory access out of bounds`. That is a property of re-entering
 * an asyncified program from outside its own event loop; no care inside the
 * operations avoids it.
 *
 * So JS never executes engine work. It stages a batch into plain memory, seals
 * it, and polls. The engine picks the batch up at a service point in the
 * main-menu loop, runs the whole thing inside its own call stack, and writes the
 * results back. Everything this file calls is a leaf — statics in, statics out.
 *
 * Three consequences the caller cannot opt out of:
 *
 *   - **Batching is the API.** One `runBatch` per round trip, which is the shape
 *     the editor already had: it queues inventory edits and applies them all at
 *     Write.
 *   - **Polling must yield.** The engine only advances between ticks, so a
 *     synchronous spin would deadlock it. `POLL_INTERVAL_MS` is comfortably over
 *     the ~17 ms tick at the default 60 fps.
 *   - **Only the main menu services the mailbox.** A batch sealed while the
 *     player is in a game sits forever. The shell's "engine is booted and idle
 *     at the menu" gate is therefore load-bearing, not a nicety.
 *
 * ## What a round trip costs
 *
 * Afterwards the engine is holding a loaded game while still spinning in the
 * main-menu loop, with the isometric window shown underneath. It is *spent*: not
 * a playable session, and not to be asked for a second one. The shell's only
 * route back to a playable engine is a reload — the same thing HUD "Quit" does.
 */

/**
 * Which equipment slot a wield/unwield op names (engine commit `d13a52bb`).
 * Not the same numbering as anything else in this codec, so it is named.
 */
export const EQUIP_SLOT_LEFT_HAND = 0;
export const EQUIP_SLOT_RIGHT_HAND = 1;
export const EQUIP_SLOT_ARMOR = 2;

export type EquipSlot =
  | typeof EQUIP_SLOT_LEFT_HAND
  | typeof EQUIP_SLOT_RIGHT_HAND
  | typeof EQUIP_SLOT_ARMOR;

export const EQUIP_SLOT_NAMES: Record<EquipSlot, string> = {
  [EQUIP_SLOT_LEFT_HAND]: 'left hand',
  [EQUIP_SLOT_RIGHT_HAND]: 'right hand',
  [EQUIP_SLOT_ARMOR]: 'worn armour',
};

/** One operation in a batch. `slot` is 1-based, matching SAVEGAME/SLOTnn. */
export type BridgeOp =
  | { kind: 'load'; slot: number }
  | { kind: 'give'; pid: number; count: number; owner?: number }
  | { kind: 'remove'; pid: number; count: number; owner?: number }
  | { kind: 'snapshotInventory' }
  /** Takes whatever is in `equipSlot` off, leaving it in the inventory. */
  | { kind: 'unwield'; equipSlot: EquipSlot }
  /** Puts `pid` into `equipSlot`. The item must already be in the inventory. */
  | { kind: 'wield'; pid: number; equipSlot: EquipSlot }
  | { kind: 'setSpecial'; stat: number; value: number }
  | { kind: 'setExperience'; value: number }
  | { kind: 'addSkill'; skill: number }
  | { kind: 'takePerk'; perk: number }
  | { kind: 'setCondition'; field: number; value: number }
  | { kind: 'setPcStat'; stat: number; value: number }
  | { kind: 'grantSkillPoints'; count: number }
  /** Captures loaded state into the engine-owned JSON snapshot buffer. */
  | { kind: 'snapshot' }
  | { kind: 'write'; slot: number };

export type BridgeBatchResult =
  | {
      ok: true;
      /** Per-op results, positionally. `remove` reports how many it got. */
      results: number[];
    }
  | {
      ok: false;
      /** Human-readable, already naming the op and the reason. */
      message: string;
      /** Index of the op that failed, or -1 when the batch never ran. */
      failedIndex: number;
      results: number[];
      /** The batch was sealed but nobody serviced it. See `SaveEditBridge.probe`. */
      timedOut?: boolean;
    };

/**
 * What this particular engine can be asked to do.
 *
 * The shell is built against a moving vendor: the mailbox arrived in one engine
 * commit and wield/unwield in another (`d13a52bb`), and the two are vendored at
 * different times. Rather than version-sniffing, the shell asks the module which
 * exports it has and shapes the UI around the answer — so the Exchange control
 * appears the day the newer engine is vendored and needs no other change.
 */
export interface BridgeCapabilities {
  /** `_rr_stage_wield` and `_rr_stage_unwield` are both present. */
  exchange: boolean;
  partyInventory: boolean;
  /** Engine can build and return an authority-aware semantic snapshot. */
  snapshot: boolean;
}

export type BridgeInventorySnapshot = {
  owners: { index: number; pid: number; name: string }[];
  entries: { owner: number; pid: number; count: number; flags: number }[];
};

export interface SaveEditBridge {
  /** What this engine's mailbox supports beyond the original four ops. */
  readonly capabilities: BridgeCapabilities;
  /**
   * Stage, seal, poll, collect. Never throws: a wasm trap or a protocol refusal
   * comes back as `ok: false`, because the caller's response to "the engine
   * would not do it" is the same either way — restore the slot and say so.
   *
   * Batch semantics are stop-at-first-failure, and the engine does no rollback:
   * the ops after the failure report `-100` and repairing the slot is the
   * shell's job.
   */
  runBatch(ops: BridgeOp[]): Promise<BridgeBatchResult>;
  loadInventory(slot: number): Promise<{ outcome: BridgeBatchResult; snapshot: BridgeInventorySnapshot | null }>;

  /** Reads the last completed snapshot operation. Null means unavailable. */
  readSnapshotJson(): string | null;

  /**
   * Is the engine servicing the mailbox *right now*?
   *
   * The exports being present says the engine has a mailbox; it does not say
   * anyone is reading it. Only the main-menu loop services batches, and the
   * engine is not always in it — during the opening movies, most obviously,
   * which is minutes of a first-ever boot. A batch sealed then sits unserviced
   * forever, so without this the first thing a player would learn is a two-minute
   * hang followed by a rollback.
   *
   * The probe is a batch of one deliberately invalid load. `rrBridgeProbeSlot`
   * answers an out-of-range slot from the header alone and leaves the engine
   * exactly as it found it, so this is safe to run speculatively and does not
   * spend the engine — only *completing* matters, not the op's result.
   */
  probe(): Promise<boolean>;
}

/** Comfortably longer than one 60 fps tick, so a poll never starves the engine. */
const POLL_INTERVAL_MS = 25;

/**
 * How long a batch may take before the shell stops believing in it. A load plus
 * a whole-slot write is seconds, not minutes; this is a stuck-engine guard, not
 * a performance budget.
 */
const BATCH_TIMEOUT_MS = 120_000;

/**
 * How long a liveness probe waits. A serviced batch answers on the next tick
 * (~17 ms at 60 fps), so anything past a couple of seconds means nobody is
 * reading the mailbox — not that the engine is busy.
 */
const PROBE_TIMEOUT_MS = 2_500;

/** A slot number no `save_pages` setting can make valid. The probe op's argument. */
const PROBE_INVALID_SLOT = 0;

/** The engine's cap. Staging past it returns -1. */
const MAX_BATCH_OPS = 64;

/** `kRrOpNotRun` — an op skipped because an earlier one failed. */
const OP_NOT_RUN = -100;

/**
 * The pre-mailbox direct-call exports return this in the Emscripten build.
 * Seeing it means something is still calling them, which is a wiring bug in the
 * shell rather than anything the engine did wrong — so it says so.
 */
const LEGACY_SENTINEL = -9;

const OP_ERRORS: Record<BridgeOp['kind'], Record<number, string>> = {
  load: { [-1]: 'slot is empty', [-2]: 'unsupported save version', [-3]: 'load failed' },
  give: {
    [-1]: 'unknown pid',
    [-2]: 'that pid is not an item',
    [-3]: 'the engine refused the item',
    [-4]: 'no save loaded',
    [-5]: 'the item would exceed carry weight (older engine builds only)',
    [-6]: 'that inventory owner is not in the loaded party',
  },
  remove: {
    [-1]: 'unknown pid', [-2]: 'that pid is not an item', [-4]: 'no save loaded',
    [-6]: 'that inventory owner is not in the loaded party',
  },
  snapshotInventory: { [-4]: 'no save loaded', [-7]: 'too many party members', [-8]: 'too many inventory stacks' },
  // The engine's rules pass straight through. A -3 here is the game declining —
  // a hook script veto, or a weapon with no wield art — not a shell bug, so it
  // is worded as the engine's decision rather than an error to report.
  unwield: {
    [-1]: 'nothing is equipped in that slot',
    [-3]: 'the engine would not take it off',
    [-4]: 'no save loaded',
    [-5]: 'not an equipment slot',
  },
  wield: {
    [-1]: 'the item is not in the top-level inventory',
    [-2]: "that item's type does not go in that slot",
    [-3]: 'the engine refused to equip it',
    [-4]: 'no save loaded',
    [-5]: 'not an equipment slot',
  },
  snapshot: { [-1]: 'no save loaded', [-2]: 'snapshot exceeded the engine size limit' },
  write: { [-1]: 'write failed', [-2]: 'no save loaded' },
  setSpecial: {
    [-1]: 'the engine refuses that stat', [-2]: 'below the engine minimum', [-3]: 'above the engine maximum',
    [-4]: 'no save loaded', [-5]: 'not a SPECIAL stat', [-6]: 'conditional modifiers prevented that exact value',
  },
  setExperience: {
    [-2]: 'below the engine minimum', [-3]: 'above the engine maximum', [-4]: 'no save loaded',
    [-5]: 'invalid experience field', [-6]: 'Engine rules does not lower levels or experience',
  },
  addSkill: {
    [-1]: 'the engine refused the skill increase', [-3]: 'the skill is already capped',
    [-4]: 'not enough unspent skill points', [-5]: 'invalid skill or character',
  },
  takePerk: {
    [-1]: 'no perk selection is owed', [-2]: 'Tag! and Mutate! require an interactive second choice',
    [-3]: 'the character does not qualify at the owed perk level', [-4]: 'no save loaded',
  },
  setCondition: {
    [-2]: 'below the engine minimum', [-3]: 'above the engine maximum', [-4]: 'no save loaded',
    [-5]: 'invalid condition field', [-6]: 'the engine could not reach that exact value',
  },
  setPcStat: {
    [-2]: 'below the engine minimum', [-3]: 'above the engine maximum', [-4]: 'no save loaded',
    [-5]: 'Engine rules only exposes reputation and karma through this control',
  },
  grantSkillPoints: {
    [-2]: 'the grant must be at least one point', [-3]: 'the skill-point total would overflow',
    [-4]: 'no save loaded',
  },
};

const OP_LABELS: Record<BridgeOp['kind'], string> = {
  load: 'loading the slot',
  give: 'giving the item',
  remove: 'removing the item',
  unwield: 'taking the equipped item off',
  wield: 'equipping the item',
  snapshot: 'capturing engine state',
  write: 'writing the slot',
  setSpecial: 'setting SPECIAL',
  setExperience: 'applying experience and level progression',
  addSkill: 'spending a skill point',
  takePerk: 'taking the perk',
  snapshotInventory: 'reading party inventories',
  setCondition: 'setting the character condition',
  setPcStat: 'setting reputation or karma',
  grantSkillPoints: 'granting unspent skill points',
};

export function bridgeErrorText(kind: BridgeOp['kind'], code: number): string {
  if (code === OP_NOT_RUN) return 'did not run (an earlier step failed)';
  if (code === LEGACY_SENTINEL) {
    return 'the engine rejected a pre-mailbox call — the shell is wired to the wrong bridge API';
  }
  return OP_ERRORS[kind]?.[code] ?? `${kind} failed with code ${code}`;
}

/** Did this op's result code mean success? `remove` reports a count, not a 0. */
function opSucceeded(kind: BridgeOp['kind'], code: number): boolean {
  return kind === 'remove' || kind === 'snapshotInventory' ? code >= 0 : code === 0;
}

/**
 * The shape `resolveSaveEditBridge` needs from an Emscripten module.
 *
 * Structural rather than `EmModule` so this file stays independent of the boot
 * path: the bridge is a set of integer functions, and nothing about resolving
 * them depends on how the module got here.
 */
export interface BridgeExports {
  /** Present only when the build lists it in EXPORTED_RUNTIME_METHODS. */
  cwrap?: (name: string, ret: string | null, args: string[]) => (...args: number[]) => number;
  _rr_stage_load?: (slot: number) => number;
  _rr_stage_give?: (pid: number, count: number) => number;
  _rr_stage_remove?: (pid: number, count: number) => number;
  _rr_stage_give_to?: (owner: number, pid: number, count: number) => number;
  _rr_stage_remove_from?: (owner: number, pid: number, count: number) => number;
  _rr_stage_snapshot_inventory?: () => number;
  /** Engine `d13a52bb` and later only — see `BridgeCapabilities`. */
  _rr_stage_unwield?: (equipSlot: number) => number;
  /** Engine `d13a52bb` and later only. */
  _rr_stage_wield?: (pid: number, equipSlot: number) => number;
  _rr_stage_write?: (slot: number) => number;
  _rr_stage_set_special?: (stat: number, value: number) => number;
  _rr_stage_set_experience?: (value: number) => number;
  _rr_stage_add_skill?: (skill: number) => number;
  _rr_stage_take_perk?: (perk: number) => number;
  _rr_stage_set_condition?: (field: number, value: number) => number;
  _rr_stage_set_pc_stat?: (stat: number, value: number) => number;
  _rr_stage_grant_skill_points?: (count: number) => number;
  _rr_stage_snapshot?: () => number;
  _rr_snapshot_ptr?: () => number;
  _rr_snapshot_size?: () => number;
  HEAPU8?: Uint8Array;
  _rr_submit?: () => number;
  _rr_status?: () => number;
  _rr_op_result?: (index: number) => number;
  _rr_reset?: () => number;
  _rr_bridge_abi?: () => number;
  _rr_inventory_owner_count?: () => number;
  _rr_inventory_owner_pid?: (owner: number) => number;
  _rr_inventory_owner_name_char?: (owner: number, index: number) => number;
  _rr_inventory_entry_count?: () => number;
  _rr_inventory_entry_owner?: (index: number) => number;
  _rr_inventory_entry_pid?: (index: number) => number;
  _rr_inventory_entry_count_at?: (index: number) => number;
  _rr_inventory_entry_flags?: (index: number) => number;
  _rr_save_total_slots?: () => number;
  _rr_save_slots_per_page?: () => number;
  _rr_save_quicksave_count?: () => number;
  _rr_save_autosave_start?: () => number;
  _rr_save_autosave_count?: () => number;
}

export function resolveSaveTopology(module: BridgeExports | null | undefined): import('../saves').SaveTopology | null {
  if (!module) return null;
  const calls = [
    module._rr_save_total_slots,
    module._rr_save_slots_per_page,
    module._rr_save_quicksave_count,
    module._rr_save_autosave_start,
    module._rr_save_autosave_count,
  ];
  if (!calls.every((call) => typeof call === 'function')) return null;
  const [totalSlots, slotsPerPage, quicksaveCount, autosaveStart, autosaveCount] = calls.map((call) => call!());
  if (totalSlots < 1 || totalSlots > 100 || slotsPerPage < 1) return null;
  return { totalSlots, slotsPerPage, quicksaveCount, autosaveStart, autosaveCount };
}

/**
 * The mailbox exports, with their arity. Every core operation must be present: a build with
 * some of them is not one we know how to drive, and half a mailbox — able to
 * stage but not to read results back — would strand the engine mid-batch, which
 * is worse than no bridge at all.
 */
const MAILBOX: { name: keyof BridgeExports & `_rr_${string}`; arity: number }[] = [
  { name: '_rr_stage_load', arity: 1 },
  { name: '_rr_stage_give', arity: 2 },
  { name: '_rr_stage_remove', arity: 2 },
  { name: '_rr_stage_write', arity: 1 },
  { name: '_rr_submit', arity: 0 },
  { name: '_rr_status', arity: 0 },
  { name: '_rr_op_result', arity: 1 },
  { name: '_rr_reset', arity: 0 },
  { name: '_rr_stage_set_special', arity: 2 },
  { name: '_rr_stage_set_experience', arity: 1 },
  { name: '_rr_stage_add_skill', arity: 1 },
  { name: '_rr_stage_take_perk', arity: 1 },
  { name: '_rr_stage_set_condition', arity: 2 },
  { name: '_rr_stage_set_pc_stat', arity: 2 },
  { name: '_rr_stage_grant_skill_points', arity: 1 },
];

/**
 * The exchange ops, which are *optional*. Missing them is not a broken bridge —
 * it is an older engine, and the shell shows the "unequip in game" note instead.
 * Both or neither: half of an exchange is an item left on the floor.
 */
const EXCHANGE_MAILBOX: { name: keyof BridgeExports & `_rr_${string}`; arity: number }[] = [
  { name: '_rr_stage_unwield', arity: 1 },
  { name: '_rr_stage_wield', arity: 2 },
];

const PARTY_MAILBOX: { name: keyof BridgeExports & `_rr_${string}`; arity: number }[] = [
  { name: '_rr_stage_give_to', arity: 3 },
  { name: '_rr_stage_remove_from', arity: 3 },
  { name: '_rr_stage_snapshot_inventory', arity: 0 },
  { name: '_rr_inventory_owner_count', arity: 0 },
  { name: '_rr_inventory_owner_pid', arity: 1 },
  { name: '_rr_inventory_owner_name_char', arity: 2 },
  { name: '_rr_inventory_entry_count', arity: 0 },
  { name: '_rr_inventory_entry_owner', arity: 1 },
  { name: '_rr_inventory_entry_pid', arity: 1 },
  { name: '_rr_inventory_entry_count_at', arity: 1 },
  { name: '_rr_inventory_entry_flags', arity: 1 },
];

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * Resolves the bridge from a booted engine module, or null when this engine does
 * not have a mailbox.
 *
 * Null covers every engine vendored before `388373a1`, including the two that
 * shipped the direct-call API — those exports are still present and still
 * exported, but calling them from JS is precisely what the mailbox exists to
 * avoid, so their presence is not evidence of a usable bridge. The Inventory tab
 * renders its disabled placeholder and nothing else in the shell has to know
 * which engine it booted.
 *
 * `cwrap` is used when the build exports it as a runtime method, because that is
 * the documented way in. It usually is not: the web build's
 * `EXPORTED_RUNTIME_METHODS` is `callMain,addRunDependency,removeRunDependency`,
 * and `EMSCRIPTEN_KEEPALIVE` lands each function on the module directly as
 * `Module['_rr_stage_load']`. Every argument and return is a plain int, so
 * calling the export is exactly what `cwrap` would produce.
 */
export function resolveSaveEditBridge(module: BridgeExports | null | undefined): SaveEditBridge | null {
  if (!module) return null;
  if (typeof module._rr_bridge_abi !== 'function' || module._rr_bridge_abi() < 4) return null;
  if (!MAILBOX.every(({ name }) => typeof module[name] === 'function')) return null;

  const bind = (name: keyof BridgeExports & `_rr_${string}`, arity: number): ((...args: number[]) => number) =>
    typeof module.cwrap === 'function'
      ? module.cwrap(name.slice(1), 'number', Array<string>(arity).fill('number'))
      : (module[name] as (...args: number[]) => number);

  const exchange = EXCHANGE_MAILBOX.every(({ name }) => typeof module[name] === 'function');
  const partyInventory = PARTY_MAILBOX.every(({ name }) => typeof module[name] === 'function');
  const snapshot = typeof module._rr_stage_snapshot === 'function'
    && typeof module._rr_snapshot_ptr === 'function'
    && typeof module._rr_snapshot_size === 'function'
    && module.HEAPU8 instanceof Uint8Array;

  const stageLoad = bind('_rr_stage_load', 1);
  const stageGive = bind('_rr_stage_give', 2);
  const stageRemove = bind('_rr_stage_remove', 2);
  const stageGiveTo = partyInventory ? bind('_rr_stage_give_to', 3) : null;
  const stageRemoveFrom = partyInventory ? bind('_rr_stage_remove_from', 3) : null;
  const stageSnapshotInventory = partyInventory ? bind('_rr_stage_snapshot_inventory', 0) : null;
  const stageWrite = bind('_rr_stage_write', 1);
  const stageUnwield = exchange ? bind('_rr_stage_unwield', 1) : null;
  const stageWield = exchange ? bind('_rr_stage_wield', 2) : null;
  const stageSetSpecial = bind('_rr_stage_set_special', 2);
  const stageSetExperience = bind('_rr_stage_set_experience', 1);
  const stageAddSkill = bind('_rr_stage_add_skill', 1);
  const stageTakePerk = bind('_rr_stage_take_perk', 1);
  const stageSetCondition = bind('_rr_stage_set_condition', 2);
  const stageSetPcStat = bind('_rr_stage_set_pc_stat', 2);
  const stageGrantSkillPoints = bind('_rr_stage_grant_skill_points', 1);
  const stageSnapshot = snapshot ? module._rr_stage_snapshot!.bind(module) : null;
  const submit = bind('_rr_submit', 0);
  const status = bind('_rr_status', 0);
  const opResult = bind('_rr_op_result', 1);
  const reset = bind('_rr_reset', 0);

  const stage = (op: BridgeOp): number => {
    switch (op.kind) {
      case 'load':
        return stageLoad(op.slot);
      case 'give':
        return (op.owner ?? 0) === 0
          ? stageGive(op.pid, op.count)
          : stageGiveTo ? stageGiveTo(op.owner!, op.pid, op.count) : -1;
      case 'remove':
        return (op.owner ?? 0) === 0
          ? stageRemove(op.pid, op.count)
          : stageRemoveFrom ? stageRemoveFrom(op.owner!, op.pid, op.count) : -1;
      case 'snapshotInventory':
        return stageSnapshotInventory ? stageSnapshotInventory() : -1;
      case 'unwield':
        // -1 is the engine's own "would not take any more"; reusing it here for
        // "this engine has no such op" keeps one refusal path in `execute`.
        return stageUnwield ? stageUnwield(op.equipSlot) : -1;
      case 'wield':
        return stageWield ? stageWield(op.pid, op.equipSlot) : -1;
      case 'snapshot':
        return stageSnapshot ? stageSnapshot() : -1;
      case 'write':
        return stageWrite(op.slot);
      case 'setSpecial':
        return stageSetSpecial(op.stat, op.value);
      case 'setExperience':
        return stageSetExperience(op.value);
      case 'addSkill':
        return stageAddSkill(op.skill);
      case 'takePerk':
        return stageTakePerk(op.perk);
      case 'setCondition':
        return stageSetCondition(op.field, op.value);
      case 'setPcStat':
        return stageSetPcStat(op.stat, op.value);
      case 'grantSkillPoints':
        return stageGrantSkillPoints(op.count);
    }
  };

  const failed = (message: string, failedIndex = -1, results: number[] = []): BridgeBatchResult => ({
    ok: false,
    message,
    failedIndex,
    results,
  });

  const execute = async (ops: BridgeOp[], timeoutMs: number): Promise<BridgeBatchResult> => {
    {
      if (!ops.length) return { ok: true, results: [] };
      if (ops.length > MAX_BATCH_OPS) {
        return failed(`too many operations for one batch (${ops.length}, the engine takes ${MAX_BATCH_OPS})`);
      }

      try {
        // A previous batch's results are still sitting in the mailbox; clearing
        // is how it goes back to accepting ops. -1 here means one is mid-flight,
        // which the shell's own gating should already have prevented.
        if (reset() < 0) {
          return failed('the engine is already running a batch');
        }

        for (let i = 0; i < ops.length; i++) {
          const index = stage(ops[i]!);
          if (index < 0) {
            return failed(
              index === -1
                ? 'the engine would not take any more operations (batch full)'
                : 'the engine would not accept the batch (it was not staging)'
            );
          }
          // Results are read back positionally, so a batch whose indices do not
          // match the order staged would attribute failures to the wrong op.
          if (index !== i) {
            return failed(`the engine staged operation ${i} at index ${index}; the batch is out of step`);
          }
        }

        if (submit() < 0) return failed('the engine refused to start the batch');

        // The engine only makes progress between its own ticks, so this must
        // yield rather than spin. Polling begins strictly after the seal:
        // rr_status() reads 0 both before staging and while running.
        const deadline = Date.now() + timeoutMs;
        let st = 0;
        for (;;) {
          st = status();
          if (st !== 0) break;
          if (Date.now() > deadline) {
            // The engine is either wedged or was never in a position to service
            // the mailbox (only the main-menu loop does, so a batch sealed during
            // the opening movies or in a game sits forever). Nothing here can
            // cancel a sealed batch, so the caller restores the slot and the
            // engine needs restarting.
            return {
              ...failed(
                `the engine did not finish the batch within ${Math.round(timeoutMs / 1000)}s `
                  + '(it was not at the main menu). Restart the reactor before trying again'
              ),
              timedOut: true,
            } as BridgeBatchResult;
          }
          await sleep(POLL_INTERVAL_MS);
        }

        const results = ops.map((_, i) => opResult(i));
        if (st === 1) return { ok: true, results };

        // `rr_status` gives the first failing code; the index it belongs to is
        // the first op whose own result is not a success for its kind.
        const failedIndex = ops.findIndex((op, i) => !opSucceeded(op.kind, results[i] ?? OP_NOT_RUN));
        const op = failedIndex >= 0 ? ops[failedIndex]! : null;
        const code = failedIndex >= 0 ? results[failedIndex]! : st;
        return failed(
          op
            ? `${OP_LABELS[op.kind]} failed: ${bridgeErrorText(op.kind, code)}`
            : `the batch failed with code ${st}`,
          failedIndex,
          results
        );
      } catch (e) {
        // The mailbox exports are leaves and should not be able to trap, but an
        // escaping trap here would abandon a half-written slot. It is a failed
        // batch like any other.
        return failed(`the engine trapped: ${(e as Error)?.message ?? e}`);
      }
    }
  };

  return {
    capabilities: { exchange, partyInventory, snapshot },
    runBatch: (ops) => execute(ops, BATCH_TIMEOUT_MS),
    async loadInventory(slot): Promise<{ outcome: BridgeBatchResult; snapshot: BridgeInventorySnapshot | null }> {
      if (!partyInventory) return { outcome: failed('this engine cannot inspect party inventories'), snapshot: null };
      const outcome = await execute([{ kind: 'load', slot }, { kind: 'snapshotInventory' }], BATCH_TIMEOUT_MS);
      if (!outcome.ok) return { outcome, snapshot: null };
      const call = (name: keyof BridgeExports, ...args: number[]): number =>
        (module[name] as (...values: number[]) => number)(...args);
      const owners: BridgeInventorySnapshot['owners'] = [];
      for (let owner = 0; owner < call('_rr_inventory_owner_count'); owner++) {
        let name = '';
        for (let index = 0; index < 64; index++) {
          const char = call('_rr_inventory_owner_name_char', owner, index);
          if (!char) break;
          name += String.fromCharCode(char);
        }
        owners.push({ index: owner, pid: call('_rr_inventory_owner_pid', owner), name });
      }
      const entries: BridgeInventorySnapshot['entries'] = [];
      for (let index = 0; index < call('_rr_inventory_entry_count'); index++) {
        entries.push({
          owner: call('_rr_inventory_entry_owner', index),
          pid: call('_rr_inventory_entry_pid', index),
          count: call('_rr_inventory_entry_count_at', index),
          flags: call('_rr_inventory_entry_flags', index),
        });
      }
      return { outcome, snapshot: { owners, entries } };
    },
    readSnapshotJson(): string | null {
      if (!snapshot) return null;
      const size = module._rr_snapshot_size!();
      const ptr = module._rr_snapshot_ptr!();
      if (size <= 0 || ptr <= 0 || ptr + size > module.HEAPU8!.length) return null;
      return new TextDecoder().decode(module.HEAPU8!.subarray(ptr, ptr + size));
    },
    async probe(): Promise<boolean> {
      const result = await execute([{ kind: 'load', slot: PROBE_INVALID_SLOT }], PROBE_TIMEOUT_MS);
      // Only whether it *finished* matters. The op itself is expected to fail
      // with -1 (out of range), which is precisely why it is safe to run.
      return result.ok || !result.timedOut;
    },
  };
}
